// C1 - Ünite 4: Complex Sentences & Report Writing
// Hedef: Relative clauses, participle clauses, formal linking

export const C1_UNIT_4 = {
  'C1-U4-L1': {
    id: 'C1-U4-L1',
    title: 'Relative Clauses (who, which, that)',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Relative Clauses (Sıfat Cümlecikleri)',
          explanation: 'İsimleri açıklayan veya tanımlayan cümlecikler. "who" kişiler, "which" nesneler, "that" her ikisi için.',
          table: {
            headers: ['Zamir', 'Kullanım', 'Örnek'],
            rows: [
              ['who', 'Kişiler için', 'The passenger who sat in 14A...'],
              ['which', 'Nesneler/hayvanlar', 'The plane which landed...'],
              ['that', 'Kişi ve nesne', 'The crew that/who helped...'],
              ['whose', 'İyelik', 'The passenger whose bag was lost...'],
              ['where', 'Yer', 'The airport where we landed...'],
              ['when', 'Zaman', 'The moment when we took off...']
            ]
          }
        },
        {
          type: 'tip',
          content: '💡 Defining clause = Gerekli bilgi (virgülsüz) | Non-defining = Ekstra bilgi (virgüllü)'
        },
        {
          type: 'example',
          title: '✈️ Havacılık Örnekleri',
          items: [
            { en: 'The captain, who has 20 years of experience, made the announcement.', tr: '20 yıllık deneyime sahip kaptan anonsu yaptı.' },
            { en: 'The flight which was delayed has now departed.', tr: 'Geciken uçuş şimdi kalktı.' },
            { en: 'This is the gate where we will board.', tr: 'Buradan bineceğiz.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'The passenger ___ luggage was lost filed a complaint.',
          options: ['who', 'which', 'whose', 'that'],
          correctIndex: 2,
          explanation: 'İyelik için "whose" kullanılır: whose luggage = bavulu kaybolan'
        },
        {
          type: 'fillBlank',
          sentences: [
            { text: 'The crew member ___ helped me was very kind.', answer: 'who' },
            { text: 'The aircraft ___ we flew was a Boeing 777.', answer: 'which' }
          ]
        }
      ]
    }
  },

  'C1-U4-L2': {
    id: 'C1-U4-L2',
    title: 'Participle Clauses (-ing, -ed)',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Participle Clauses',
          explanation: 'Cümleleri kısaltmak ve akıcılık sağlamak için. -ing (aktif) veya -ed (pasif) ile başlar.',
          table: {
            headers: ['Tip', 'Örnek', 'Tam Hali'],
            rows: [
              ['Present Participle (-ing)', 'Hearing the announcement, I stood up.', 'When I heard the announcement, I stood up.'],
              ['Past Participle (-ed)', 'Delayed by weather, the flight departed late.', 'Because it was delayed by weather...'],
              ['Perfect Participle (having + V3)', 'Having completed the check, we took off.', 'After we had completed the check...'],
              ['Passive (being + V3)', 'Being informed of the delay, passengers waited.', 'Because they were informed...']
            ]
          }
        },
        {
          type: 'vocabulary',
          title: '✈️ Havacılık Participle Cümleleri',
          words: [
            { en: 'Boarding the aircraft, passengers showed their passes.', tr: 'Uçağa binerken yolcular kartlarını gösterdi.' },
            { en: 'Served with a smile, the meal was appreciated.', tr: 'Gülümseyerek servis edilen yemek beğenildi.' },
            { en: 'Having landed safely, the crew relaxed.', tr: 'Güvenle indikten sonra ekip rahatladı.' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: '___ the turbulence, the cabin crew secured the carts.',
          options: ['Experience', 'Experiencing', 'Experienced', 'To experience'],
          correctIndex: 1,
          explanation: 'Aynı anda gerçekleşen aktif eylem için "-ing" (Present Participle) kullanılır.'
        }
      ]
    }
  },

  'C1-U4-L3': {
    id: 'C1-U4-L3',
    title: 'Inversion for Emphasis',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Inversion (Devrik Cümle)',
          explanation: 'Vurgu için cümle yapısını değiştirmek. Resmi ve yazılı dilde kullanılır.',
          table: {
            headers: ['İfade', 'Yapı', 'Örnek'],
            rows: [
              ['Never', 'Never + aux + S + V', 'Never have I seen such turbulence.'],
              ['Rarely', 'Rarely + aux + S + V', 'Rarely do we experience such delays.'],
              ['Not only... but also', 'Not only + aux + S + V', 'Not only did she help, but also...'],
              ['Only after/when', 'Only after + clause + aux + S + V', 'Only after landing did we relax.'],
              ['Under no circumstances', 'Under no circumstances + aux + S + V', 'Under no circumstances should you...']
            ]
          }
        },
        {
          type: 'tip',
          content: '💡 Inversion formal/written English içindir. Raporlarda ve anonslarda kullanılır.'
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'Never ___ such professional service.',
          options: ['I have experienced', 'have I experienced', 'I experienced', 'did I experienced'],
          correctIndex: 1,
          explanation: '"Never" ile inversion: Never + have + I + V3'
        }
      ]
    }
  },

  'C1-U4-L4': {
    id: 'C1-U4-L4',
    title: 'Formal Linking Words',
    type: 'lesson',
    content: {
      sections: [
        {
          type: 'grammar',
          title: '📚 Formal Bağlaçlar',
          explanation: 'Profesyonel yazı ve raporlarda kullanılan bağlaçlar.',
          table: {
            headers: ['Bağlaç', 'Anlam', 'Örnek'],
            rows: [
              ['Furthermore', 'Ayrıca', 'Furthermore, the delay was unavoidable.'],
              ['Moreover', 'Üstek, dahasi', 'Moreover, safety is our priority.'],
              ['Nevertheless', 'Bununla birlikte', 'Nevertheless, we apologize.'],
              ['Consequently', 'Sonuç olarak', 'Consequently, the flight was cancelled.'],
              ['In addition', 'Ek olarak', 'In addition, refreshments were served.'],
              ['Therefore', 'Bu nedenle', 'Therefore, we ask for your patience.'],
              ['However', 'Ancak', 'However, alternatives were offered.'],
              ['Subsequently', 'Ardından', 'Subsequently, the issue was resolved.']
            ]
          }
        },
        {
          type: 'vocabulary',
          title: '✈️ Rapor Yazımı İfadeleri',
          words: [
            { en: 'As a result of...', tr: '... sonucu olarak' },
            { en: 'Due to...', tr: '... nedeniyle' },
            { en: 'In accordance with...', tr: '... uyarınca' },
            { en: 'With regard to...', tr: '... ile ilgili olarak' }
          ]
        }
      ],
      exercises: [
        {
          type: 'multipleChoice',
          question: 'The weather was severe. ___, the flight was delayed.',
          options: ['But', 'Consequently', 'Although', 'Despite'],
          correctIndex: 1,
          explanation: 'Sonuç bildirmek için "Consequently" (Sonuç olarak) kullanılır.'
        }
      ]
    }
  },

  'C1-U4-L5': {
    id: 'C1-U4-L5',
    title: 'Uygulama: Profesyonel Rapor',
    type: 'practice',
    content: {
      sections: [
        {
          type: 'scenario',
          title: '📝 Olay Raporu Örneği',
          dialogues: [
            {
              speaker: 'Rapor',
              text: 'The passenger, who was seated in row 15, reported feeling unwell. Having informed the purser, the cabin crew provided medical assistance. Consequently, the passenger recovered before landing. Furthermore, the incident was documented in accordance with company policy.',
              translation: '15. sırada oturan yolcu kendini kötü hissettiğini bildirdi. Kabin amirini bilgilendirdikten sonra ekip tıbbi yardım sağladı. Sonuç olarak, yolcu iniş öncesi iyileşti. Ayrıca, olay şirket politikasına uygun olarak belgelendi.'
            }
          ]
        }
      ],
      exercises: [
        {
          type: 'fillBlank',
          sentences: [
            { text: 'The crew member, ___ noticed the issue, reported it immediately.', answer: 'who' },
            { text: '___ the incident, no injuries occurred.', answer: 'Despite' },
            { text: '___, all safety protocols were followed.', answer: 'Furthermore' }
          ]
        }
      ]
    }
  },

  'C1-U4-EXAM': {
    id: 'C1-U4-EXAM',
    title: '📝 Ünite 4 Sınavı',
    type: 'exam',
    content: {
      timeLimit: 20,
      passingScore: 80,
      questions: [
        {
          type: 'multipleChoice',
          question: 'The passenger ___ bag was lost received compensation.',
          options: ['who', 'which', 'whose', 'whom'],
          correctIndex: 2
        },
        {
          type: 'multipleChoice',
          question: '___ the announcement, the crew prepared for landing.',
          options: ['Heard', 'Hearing', 'To hear', 'Having hear'],
          correctIndex: 1
        },
        {
          type: 'multipleChoice',
          question: 'Never ___ such a smooth landing.',
          options: ['I experienced', 'have I experienced', 'I have experienced', 'did I experience'],
          correctIndex: 1
        }
      ]
    }
  }
};

export default C1_UNIT_4;
